import React from 'react';

import { Button, Divider, Group, useMantineTheme } from '@mantine/core';
import { IconChevronLeft } from '@tabler/icons-react';
import { AxiosError, AxiosResponse } from 'axios';
import useDeletePackage from 'hooks/package/useDeletePackage';
import { UseMutationResult } from 'react-query';
import { ErrorMessage, Package, PackageWrite } from 'types';
import notificationHandler from 'utils/notifications';

import usePackageDrawerStyles from '../styles';
import PackageDrawerState, { PackageType } from '../types';

interface IFooterProps {
  state: PackageDrawerState;
  setState: React.Dispatch<React.SetStateAction<PackageDrawerState>>;
  closeDrawer: () => void;
  formId: string;
  packageId: string;
  createPackageMutation: UseMutationResult<AxiosResponse<Package>, AxiosError<ErrorMessage>, PackageWrite>;
  updatePackageMutation: UseMutationResult<AxiosResponse<Package>, AxiosError<ErrorMessage>, PackageWrite>;
  hasBillingPlans: boolean;
  isArchived?: boolean;
  createWarningModalHandlers: {
    readonly open: () => void;
    readonly close: () => void;
    readonly toggle: () => void;
  };
  permission?: boolean;
  onStepChange?: (step: PackageDrawerState) => void;
  packageType?: PackageType;
}

const Footer = ({
  state,
  setState,
  closeDrawer,
  formId,
  packageId,
  createPackageMutation,
  updatePackageMutation,
  hasBillingPlans,
  createWarningModalHandlers,
  isArchived = false,
  permission = true,
  onStepChange = () => {},
  packageType,
}: IFooterProps): JSX.Element => {
  const theme = useMantineTheme();
  const { classes } = usePackageDrawerStyles();
  const { successNotification, errorNotification } = notificationHandler();

  const deletePackageMutation = useDeletePackage();

  const handleCreatePackage = (): void => {
    setState('create-package');
  };
  const handleEditPackage = (): void => {
    setState('edit-package');
  };

  const handleEditBillingPlans = (): void => {
    setState('edit-billing');
  };

  const handleViewPackage = (): void => {
    setState('view-package');
  };

  const handleCancelCreation = (): void => {
    deletePackageMutation.mutate(packageId, {
      onSuccess: () => {
        closeDrawer();
      },
      onError: error => {
        errorNotification('Something went wrong', error.response?.data.message);
      },
    });
  };

  const handleFinishCreation = (): void => {
    if (!hasBillingPlans) {
      createWarningModalHandlers.open();
    } else {
      closeDrawer();
      successNotification('Package creation successful');
    }
  };

  if (state === 'view-package') {
    return (
      <div className={classes.content}>
        <Group position='right' px='md' py='xs'>
          <Button
            onClick={handleEditPackage}
            disabled={!permission || isArchived}
            data-cy='editPackageButton'
          >
            Edit Package
          </Button>
        </Group>
        <Divider orientation='vertical' />
        <Group position='right' px='md' py='xs'>
          <Button
            onClick={handleEditBillingPlans}
            disabled={!permission || isArchived}
            data-cy='editBillingPlansButton'
          >
            Edit Billing Plans
          </Button>
        </Group>
      </div>
    );
  }

  if (state === 'select-type') {
    return (
      <div className={classes.content}>
        <Group position='right' px='md' py='xs'>
          <Button variant='subtle' onClick={closeDrawer}>
            Cancel
          </Button>
          <Button
            disabled={!packageType}
            onClick={() => onStepChange('edit-package')}
            data-cy='continueToConfigureButton'
          >
            Continue
          </Button>
        </Group>
        <Divider orientation='vertical' />
      </div>
    );
  }

  if (state === 'create-package') {
    return (
      <div className={classes.content}>
        <Group position='right' px='md' py='xs'>
          <Button variant='subtle' onClick={() => onStepChange('select-type')}>
            Cancel
          </Button>
          <Button
            form={formId}
            type='submit'
            loading={createPackageMutation.isLoading}
            data-cy='nextButtonPackage'
          >
            Next
          </Button>
        </Group>
        <Divider orientation='vertical' />
      </div>
    );
  }

  if (state === 'create-billing') {
    return (
      <div className={classes.content}>
        <Group position='right' px='md' py='xs' />
        <Divider orientation='vertical' />
        <Group position='apart' px='md' py='xs'>
          <Button
            variant='light'
            leftIcon={<IconChevronLeft size={theme.fontSizes.lg} />}
            onClick={handleCreatePackage}
          >
            Back
          </Button>

          <Group>
            <Button
              variant='subtle'
              onClick={handleCancelCreation}
              loading={deletePackageMutation.isLoading}
            >
              Cancel
            </Button>
            <Button onClick={handleFinishCreation} data-cy='finishPackageCreationButton'>
              Finish
            </Button>
          </Group>
        </Group>
      </div>
    );
  }

  if (state === 'edit-package') {
    return (
      <div className={classes.content}>
        <Group position='right' px='md' py='xs'>
          <Button variant='subtle' onClick={handleViewPackage}>
            Cancel
          </Button>
          <Button
            form={formId}
            type='submit'
            loading={updatePackageMutation.isLoading}
            data-cy='savePackageEdit'
          >
            Save
          </Button>
        </Group>
        <Divider orientation='vertical' />
      </div>
    );
  }

  if (state === 'edit-billing') {
    return (
      <div className={classes.content}>
        <Group position='right' px='md' py='xs' />
        <Divider orientation='vertical' />
        <Group position='right' px='md' py='xs'>
          <Button variant='subtle' onClick={handleViewPackage}>
            Cancel
          </Button>
          <Button onClick={closeDrawer} data-cy='closeBillingPlanDrawer'>
            Close
          </Button>
        </Group>
      </div>
    );
  }

  return (
    <div className={classes.content}>
      <Group position='right' p='md'>
        Left
      </Group>
      <Divider orientation='vertical' />
      <Group position='right' p='md'>
        Right
      </Group>
    </div>
  );
};
export default Footer;
